import React, { useState, useEffect } from 'react';
import { BaseCard } from '../UI/BaseCard';
import { BaseInput } from '../UI/BaseInput';
import { BaseButton } from '../UI/BaseButton';
import { BaseSelect } from '../UI/BaseSelect';
import { __ } from '@wordpress/i18n';

import { useForm } from '../../hooks/useForm';
import { maxValueValidator, minValueValidator, numbersValidator, requiredFieldValidator } from '../../utils/validation';

export const CalculatorChoco = () => {
  const [result, setResult] = useState(null);

  const { formValue, formErrors, setFormFieldValue, onFormSubmit } = useForm({
    form: {
      chocoKind: '',
      grams: '',
      weight: ''
    },
    rules: {
      chocoKind: requiredFieldValidator(),
      grams: [maxValueValidator(500), minValueValidator(0), numbersValidator(), requiredFieldValidator()],
      weight: [minValueValidator(0), numbersValidator(), requiredFieldValidator()]
    },
    onSubmit: () => {
      getCalculatorResult(formValue);
    }
  });

  const chocoValues = [
    {
      value: '0',
      text: __('White', 'cooperpetcare')
    },
    {
      value: '2.26',
      text: __('Milk', 'cooperpetcare')
    },
    {
      value: '5.6',
      text: __('Semi-sweet', 'cooperpetcare')
    },
    {
      value: '8.46',
      text: __('60% cocoa dark', 'cooperpetcare')
    },
    {
      value: '12',
      text: __('85% cocoa dark', 'cooperpetcare')
    },
    {
      value: '15.52',
      text: __('Unsweetened', 'cooperpetcare')
    },
    {
      value: '28.46',
      text: __('Dry cocoa powder', 'cooperpetcare')
    }
  ];

  useEffect(() => {
    setResult(null);
  }, [formValue]);

  const getCalculatorResult = ({ chocoKind, grams, weight }) => {
    const chocoKindParams = chocoValues.find(({ value }) => value === chocoKind);

    const value = Number((grams * chocoKindParams.value) / weight).toFixed(1);
    let conclusion;
    if (value < 20)
      conclusion = {
        title: __('Likely none', 'cooperpetcare'),
        description: __('Possibly mild gastrointestinal upset', 'cooperpetcare'),
        status: 'ok'
      };
    if (value >= 20 && value < 50)
      conclusion = {
        title: __('Mild', 'cooperpetcare'),
        description: __('May show signs of Vomiting, Diarrhea, Shaking, Elevated Heart Rate, Increased Urination', 'cooperpetcare'),
        status: 'warning'
      };
    if (value >= 50 && value < 150)
      conclusion = {
        title: __('Moderate to severe', 'cooperpetcare'),
        description: __('Tremors, seizures, heartbeat may be irregular or rapid, potential for collapse', 'cooperpetcare'),
        status: 'caution'
      };
    if (value >= 150)
      conclusion = {
        title: __('Potentially fatal', 'cooperpetcare'),
        description: __(
          'Hot to the touch, collapse, irregular heart beat, breathing problems, racing heart, potential sudden death',
          'cooperpetcare'
        ),
        status: 'danger'
      };
    setResult(conclusion);
  };

  return (
    <div className="calculator calculator--choco">
      <BaseCard title={__('Chocolate Toxicity Calculator', 'cooperpetcare')}>
        <BaseSelect
          className="field persistent-placeholder"
          label={__('Chocolate type:', 'cooperpetcare')}
          placeholder={__('Please select an option', 'cooperpetcare')}
          persistentPlaceholder={true}
          filterDisabled={true}
          isInvalid={!!formErrors.chocoKind}
          hint={formErrors.chocoKind}
          options={chocoValues}
          value={formValue.chocoKind}
          onSelect={setFormFieldValue('chocoKind')}
        ></BaseSelect>

        <BaseInput
          className="field persistent-placeholder"
          label={__('Weight of chocolate in grams:', 'cooperpetcare')}
          placeholder={__('Weight of chocolate in grams', 'cooperpetcare')}
          type="number"
          persistentPlaceholder={true}
          isInvalid={!!formErrors.grams}
          hint={formErrors.grams}
          value={formValue.grams}
          onChange={setFormFieldValue('grams')}
        ></BaseInput>

        <BaseInput
          className="field persistent-placeholder"
          label={__('Weight of the pet (kg):', 'cooperpetcare')}
          placeholder={__('Weight of your pet in kilograms', 'cooperpetcare')}
          type="number"
          persistentPlaceholder={true}
          isInvalid={!!formErrors.weight}
          hint={formErrors.weight}
          value={formValue.weight}
          onChange={setFormFieldValue('weight')}
        ></BaseInput>

        <BaseButton className="btn-rounded" variant="warning" onClick={onFormSubmit}>
          {__('Calculate', 'cooperpetcare')}
        </BaseButton>

        {result && (
          <div className={`result  result-${result.status}`}>
            <h3 className="result-title">{result.title}</h3>
            <div className="result-description">{result.description}</div>
          </div>
        )}
      </BaseCard>
    </div>
  );
};
